import '@blueprintjs/core/lib/css/blueprint.css'
import { FC, memo } from 'react'
import { PolotnoContainer, WorkspaceWrap } from 'polotno'
import { ZoomButtons } from 'polotno/toolbar/zoom-buttons'
import { Workspace } from 'polotno/canvas/workspace'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import { Navbar } from '@blueprintjs/core'
import { usePdfEditor } from './PdfEditorProvider'
import CloseButton from './CloseButton'
import SaveButton from './SaveButton'
import AddTextButton from './AddTextButton'
import AddImageButton from './AddImageButton'
import AddShapeButton from './AddShapeButton'
import ContextToolbar from './ContextToolbar'
import ValidationErrorsTracker from './ValidationErrorsTracker'
import ChangeImageContextMenuButton from './ChangeImageContextMenuButton'
import FitImageContextMenuButton from './FitImageContextMenuButton'
import InfoOutlineIcon from '@mui/icons-material/InfoOutline'

const PdfEditor: FC = () => {
  const { store, saving, contextToolbarVisible, filePickerRef, handleFilePickerChange } =
    usePdfEditor()
  return (
    <>
      <PolotnoContainer style={{ width: '100vw', height: '100vh' }}>
        <WorkspaceWrap>
          <Navbar>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <Box>
                <CloseButton />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flex: 1,
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <AddTextButton />
                <AddImageButton />
                <AddShapeButton />
              </Box>
              <Box>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 100,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Tooltip title="Watch tutorial">
                    <Box
                      component={'a'}
                      href={'https://youtu.be/SStlq3ponLU'}
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        pointerEvents: 'all',
                        textDecoration: 'none',
                        outline: 'none',
                        color: '#606b7c',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ':hover': { textDecoration: 'none', color: 'text.primary' },
                      }}
                    >
                      <InfoOutlineIcon fontSize="medium" />
                    </Box>
                  </Tooltip>
                </Box>
                <SaveButton />
              </Box>
            </Box>
          </Navbar>
          {contextToolbarVisible && (
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
              <ContextToolbar />
            </Box>
          )}
          <Workspace
            renderOnlyActivePage
            store={store}
            components={{
              Duplicate: () => null,
              PageControls: ({ xPadding, yPadding }: { xPadding: number; yPadding: number }) => (
                <Box
                  sx={{
                    position: 'absolute',
                    top: yPadding - 8,
                    right: xPadding,
                    transform: 'translate(0%, -100%)',
                  }}
                >
                  <ValidationErrorsTracker />
                </Box>
              ),
              ImageFlip: () => <ChangeImageContextMenuButton />,
              ImageFitToBackground: () => <FitImageContextMenuButton />,
              TextAiWrite: () => null,
            }}
          />
          <ZoomButtons store={store} />
        </WorkspaceWrap>
        {saving && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
            }}
          />
        )}
      </PolotnoContainer>
      <input
        ref={filePickerRef}
        type="file"
        id="fileInput"
        accept="image/*"
        onChange={handleFilePickerChange}
        style={{ display: 'none' }}
      />
    </>
  )
}

export default memo(PdfEditor)
